import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Box, Button, FormControl, IconButton, Tooltip, Typography } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TextFieldWrapper from '../../../common/TextFieldWrapper/TextFieldWrapper';
import { Notification } from '../../../common/Notification/Notification';
import { notificationType } from '../../../types/notifications';
import { EmailImageField, IMAGE_TOOLTIP, INVALID_IMAGE_MESSAGE, loadEmailImage, uploadEmailImage } from './emailTemplateImages';

const fields: { name: EmailImageField; label: string }[] = [
    { name: 'headerImage', label: 'Email Header Image' },
    { name: 'emailIcon', label: 'Email Icon' },
];

const EmailTemplates = () => {
    const [locations, setLocations] = useState({ headerImage: '', emailIcon: '' });
    const [loading, setLoading] = useState({ headerImage: true, emailIcon: true });
    const [loadFailed, setLoadFailed] = useState({ headerImage: false, emailIcon: false });
    const [uploading, setUploading] = useState({ headerImage: false, emailIcon: false });
    const [notificationState, setNotificationState] = useState<notificationType>({ open: false, title: '', content: '' });
    const fileInputs = useRef<Partial<Record<EmailImageField, HTMLInputElement>>>({});
    const uploadInProgress = useRef({ headerImage: false, emailIcon: false });
    const mounted = useRef(false);

    const notify = (content: string, level: 'success' | 'error') => {
        if (mounted.current) setNotificationState({ open: true, title: level === 'error' ? 'Error' : 'Success', content, level });
    };

    const loadImage = async (field: EmailImageField) => {
        setLoading(previous => ({ ...previous, [field]: true }));
        try {
            const location = await loadEmailImage(field);
            if (mounted.current) {
                setLocations(previous => ({ ...previous, [field]: location }));
                setLoadFailed(previous => ({ ...previous, [field]: false }));
            }
        } catch {
            if (mounted.current) setLoadFailed(previous => ({ ...previous, [field]: true }));
            notify('The saved image location could not be loaded. Please try again.', 'error');
        } finally {
            if (mounted.current) setLoading(previous => ({ ...previous, [field]: false }));
        }
    };

    useEffect(() => {
        mounted.current = true;
        fields.forEach(({ name }) => { void loadImage(name); });
        return () => { mounted.current = false; };
        // The saved locations are loaded once when this tab opens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleUpload = async (field: EmailImageField, event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || loading[field] || loadFailed[field] || uploadInProgress.current[field]) return;

        uploadInProgress.current[field] = true;
        setUploading(previous => ({ ...previous, [field]: true }));
        try {
            const location = await uploadEmailImage(field, file);
            if (mounted.current) setLocations(previous => ({ ...previous, [field]: location }));
            notify('The image was uploaded successfully.', 'success');
        } catch (error) {
            notify(error instanceof Error && error.message === INVALID_IMAGE_MESSAGE ? INVALID_IMAGE_MESSAGE : 'The image could not be uploaded and saved. Please try again.', 'error');
        } finally {
            uploadInProgress.current[field] = false;
            if (mounted.current) setUploading(previous => ({ ...previous, [field]: false }));
        }
    };

    const copyLocation = async (location: string) => {
        try {
            await navigator.clipboard.writeText(location);
            notify('The Image location was copied successfully.', 'success');
        } catch {
            notify('The image location could not be copied. Please try again.', 'error');
        }
    };

    return (
        <Box sx={{ p: 3, maxWidth: 850 }}>
            <Typography sx={{ fontSize: 20, fontWeight: 'bold', mb: 3 }}>Email Templates</Typography>
            {fields.map(({ name, label }) => {
                const busy = loading[name] || uploading[name];
                return (
                    <FormControl key={name} fullWidth sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography component='label' htmlFor={`${name}-location`}>{label}</Typography>
                            <Tooltip title={<Box sx={{ whiteSpace: 'pre-line' }}>{IMAGE_TOOLTIP}</Box>} placement='right'>
                                <IconButton size='small' aria-label={`${label} information`}><HelpOutlineIcon fontSize='small' /></IconButton>
                            </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextFieldWrapper
                                id={`${name}-location`}
                                fullWidth
                                size='small'
                                value={locations[name]}
                                placeholder={loading[name] ? 'Loading...' : 'No file chosen'}
                                InputProps={{ readOnly: true }}
                            />
                            {locations[name] ? (
                                <Button variant='contained' onClick={() => copyLocation(locations[name])}>Copy</Button>
                            ) : (
                                <Button variant='contained' sx={{ whiteSpace: 'nowrap' }} disabled={busy || loadFailed[name]} onClick={() => fileInputs.current[name]?.click()}>
                                    {uploading[name] ? 'Uploading...' : 'Choose File'}
                                </Button>
                            )}
                        </Box>
                        {locations[name] && (
                            <Button sx={{ alignSelf: 'flex-start', mt: 1 }} disabled={busy || loadFailed[name]} onClick={() => fileInputs.current[name]?.click()}>
                                {uploading[name] ? 'Uploading...' : 'Choose File'}
                            </Button>
                        )}
                        {loadFailed[name] && <Button sx={{ alignSelf: 'flex-start' }} disabled={busy} onClick={() => loadImage(name)}>Retry loading {label}</Button>}
                        <input
                            hidden
                            type='file'
                            aria-label={`Choose ${label}`}
                            accept='.png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml'
                            disabled={busy || loadFailed[name]}
                            ref={input => { fileInputs.current[name] = input; }}
                            onChange={event => handleUpload(name, event)}
                        />
                    </FormControl>
                );
            })}
            <Notification notificationState={notificationState} setNotificationState={setNotificationState} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} />
        </Box>
    );
};

export { EmailTemplates };
